import { useEffect, useState, useEffectEvent } from 'react';
import { useMap, Rectangle } from 'react-leaflet';
import L from 'leaflet';

interface Props {
    onRectChange: (rect: L.LatLngBounds | null) => void;
    rect: L.LatLngBounds | null;
}

export default function LitterSweepLayer({ onRectChange, rect }: Props) {
    const map = useMap();
    const [startPoint, setStartPoint] = useState<L.LatLng | null>(null);
    const [currentPoint, setCurrentPoint] = useState<L.LatLng | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Effect Events - always read latest values without being reactive dependencies
    const handleMouseDown = useEffectEvent((e: MouseEvent) => {
        if (rect) return;
        const point = map.mouseEventToLatLng(e);
        setStartPoint(point);
        setCurrentPoint(point);
        setIsDrawing(true);
    });

    const handleMouseMove = useEffectEvent((e: MouseEvent) => {
        if (!isDrawing) return;
        const point = map.mouseEventToLatLng(e);
        setCurrentPoint(point);
    });

    const handleMouseUp = useEffectEvent(() => {
        if (isDrawing && startPoint && currentPoint) {
            const newRect = L.latLngBounds(startPoint, currentPoint);
            onRectChange(newRect);
            setIsDrawing(false);
            setStartPoint(null);
            setCurrentPoint(null);
        }
    });

    const handleTouchStart = useEffectEvent((e: TouchEvent) => {
        if (e.touches.length !== 1 || rect) return;
        e.preventDefault();

        const touch = e.touches[0];
        const point = map.mouseEventToLatLng(touch as any);
        setStartPoint(point);
        setCurrentPoint(point);
        setIsDrawing(true);
    });

    const handleTouchMove = useEffectEvent((e: TouchEvent) => {
        if (!isDrawing || e.touches.length !== 1) return;
        e.preventDefault();

        const touch = e.touches[0];
        const point = map.mouseEventToLatLng(touch as any);
        setCurrentPoint(point);
    });

    const handleTouchEnd = useEffectEvent((e: TouchEvent) => {
        if (!isDrawing) return;
        e.preventDefault();

        if (startPoint && currentPoint) {
            const newRect = L.latLngBounds(startPoint, currentPoint);
            onRectChange(newRect);
        }
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
    });

    useEffect(() => {
        const container = map.getContainer();

        map.dragging.disable();
        map.touchZoom.disable();
        map.doubleClickZoom.disable();
        map.scrollWheelZoom.disable();

        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: false });

        return () => {
            map.dragging.enable();
            map.touchZoom.enable();
            map.doubleClickZoom.enable();
            map.scrollWheelZoom.enable();

            container.removeEventListener('mousedown', handleMouseDown);
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [map, onRectChange]);

    const drawingRect = isDrawing && startPoint && currentPoint ? L.latLngBounds(startPoint, currentPoint) : null;

    return (
        <>
            {drawingRect && <Rectangle bounds={drawingRect} pathOptions={{ color: '#FFA726', weight: 2 }} />}
            {rect && <Rectangle bounds={rect} pathOptions={{ color: '#FFA726', weight: 2, fillOpacity: 0.5 }} />}
        </>
    );
}
